<template>
    <div class="bs-wrap" :class="{ 'is-open': isOpen }" v-click-outside="close">
        <!-- Trigger -->
        <div class="bs-trigger" @click="toggle">
            <!-- Tags (multiple) -->
            <div v-if="multiple" class="bs-tags">
                <span v-for="item in selectedItems" :key="item[trackBy]" class="bs-tag">
                    {{ item[labelKey] }}
                    <i class="fal fa-times bs-tag-remove" @click.stop="deselect(item)"></i>
                </span>
                <input
                    v-if="searchable"
                    ref="searchInput"
                    v-model="search"
                    class="bs-search-inline"
                    :placeholder="selectedItems.length ? '' : placeholder"
                    @input="onSearch"
                    @focus="open"
                />
                <span v-else-if="!selectedItems.length" class="bs-placeholder">{{ placeholder }}</span>
            </div>
            <!-- Single -->
            <div v-else class="bs-single">
                <span v-if="selectedItems.length" class="bs-single-value">{{ selectedItems[0][labelKey] }}</span>
                <span v-else class="bs-placeholder">{{ placeholder }}</span>
            </div>
            <i class="fal bs-chevron" :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>

        <!-- Dropdown -->
        <transition name="bs-dropdown">
            <div v-if="isOpen" class="bs-dropdown">
                <!-- Search (single mode) -->
                <div v-if="searchable && !multiple" class="bs-dropdown-search">
                    <i class="fal fa-search bs-dropdown-search-icon"></i>
                    <input
                        ref="searchInput"
                        v-model="search"
                        class="bs-dropdown-search-input"
                        placeholder="Rechercher..."
                        @input="onSearch"
                    />
                </div>

                <!-- Options -->
                <div class="bs-options">
                    <div
                        v-for="option in filteredOptions"
                        :key="option[trackBy]"
                        class="bs-option"
                        :class="{ 'is-selected': isSelected(option) }"
                        @click.stop="selectOption(option)"
                    >
                        <span class="bs-option-label">{{ option[labelKey] }}</span>
                        <i v-if="isSelected(option)" class="fal fa-check bs-option-check"></i>
                    </div>
                    <div v-if="filteredOptions.length === 0" class="bs-no-result">
                        {{ noResultText }}
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'BmvSelect',
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._clickOutside)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutside)
            }
        }
    },
    props: {
        value: {
            default: null
        },
        options: {
            type: Array,
            default: () => []
        },
        labelKey: {
            type: String,
            default: 'name'
        },
        trackBy: {
            type: String,
            default: 'id'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        searchable: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: ' '
        },
        noResultText: {
            type: String,
            default: 'Aucun résultat'
        }
    },
    data() {
        return {
            isOpen: false,
            search: ''
        }
    },
    computed: {
        selectedItems() {
            if (!this.value) return []
            if (this.multiple) {
                return Array.isArray(this.value) ? this.value : []
            }
            return this.value ? [this.value] : []
        },
        filteredOptions() {
            if (!this.search) return this.options
            const q = this.search.toLowerCase()
            return this.options.filter((o) =>
                (o[this.labelKey] || '').toLowerCase().includes(q)
            )
        }
    },
    methods: {
        toggle() {
            this.isOpen ? this.close() : this.open()
        },
        open() {
            this.isOpen = true
            this.$nextTick(() => {
                if (this.$refs.searchInput) this.$refs.searchInput.focus()
            })
        },
        close() {
            this.isOpen = false
            this.search = ''
        },
        isSelected(option) {
            if (this.multiple) {
                return this.selectedItems.some((s) => s[this.trackBy] === option[this.trackBy])
            }
            return this.selectedItems.length > 0 && this.selectedItems[0][this.trackBy] === option[this.trackBy]
        },
        selectOption(option) {
            if (this.multiple) {
                const exists = this.selectedItems.findIndex((s) => s[this.trackBy] === option[this.trackBy])
                let newValue
                if (exists >= 0) {
                    newValue = [...this.selectedItems]
                    newValue.splice(exists, 1)
                } else {
                    newValue = [...this.selectedItems, option]
                }
                this.$emit('input', newValue)
            } else {
                this.$emit('input', option)
                this.close()
            }
        },
        deselect(item) {
            const newValue = this.selectedItems.filter((s) => s[this.trackBy] !== item[this.trackBy])
            this.$emit('input', newValue)
        },
        onSearch() {
            // just triggers filteredOptions recompute
        }
    }
}
</script>

<style scoped>
.bs-wrap {
    position: relative;
}

.bs-trigger {
    display: flex;
    align-items: center;
    min-height: 34px;
    max-height: 64px;
    padding: 3px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    background: #f8fafc;
    cursor: pointer;
    transition: all 0.2s;
    gap: 6px;
    overflow: hidden;
}

.bs-trigger:hover {
    border-color: #cbd5e1;
}

.bs-wrap.is-open .bs-trigger {
    border-color: #0891B2;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.08);
    background: #fff;
}

.bs-chevron {
    font-size: 0.6rem;
    color: #94a3b8;
    flex-shrink: 0;
    margin-left: auto;
}

/* Single */
.bs-single {
    flex: 1;
    min-width: 0;
}

.bs-single-value {
    font-size: 0.84rem;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bs-placeholder {
    font-size: 0.84rem;
    color: #94a3b8;
}

/* Tags */
.bs-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    flex: 1;
    min-width: 0;
    align-items: center;
    overflow: hidden;
}

.bs-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: rgba(8, 145, 178, 0.1);
    color: #0891B2;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.bs-tag-remove {
    font-size: 0.6rem;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s;
}

.bs-tag-remove:hover {
    opacity: 1;
}

.bs-search-inline {
    border: none;
    outline: none;
    background: none;
    font-size: 0.84rem;
    color: #0f172a;
    min-width: 60px;
    flex: 1;
    padding: 0;
}

/* Dropdown */
.bs-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    /* Above Leaflet's .leaflet-top/.leaflet-bottom (1000) so the list
     * isn't covered by the map when the select sits above it. */
    z-index: 1020;
    overflow: hidden;
}

.bs-dropdown-enter-active,
.bs-dropdown-leave-active {
    transition: all 0.15s ease;
}

.bs-dropdown-enter,
.bs-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

/* Search in dropdown */
.bs-dropdown-search {
    position: relative;
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
}

.bs-dropdown-search-icon {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.75rem;
}

.bs-dropdown-search-input {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 10px 6px 28px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
}

.bs-dropdown-search-input:focus {
    border-color: #0891B2;
    background: #fff;
}

/* Options */
.bs-options {
    max-height: 220px;
    overflow-y: auto;
    padding: 4px;
}

.bs-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 0.84rem;
    color: #334155;
    cursor: pointer;
    transition: background 0.12s;
}

.bs-option:hover {
    background: #f0f7ff;
}

.bs-option.is-selected {
    background: rgba(8, 145, 178, 0.06);
    color: #0891B2;
    font-weight: 600;
}

.bs-option-check {
    font-size: 0.75rem;
    color: #0891B2;
}

.bs-option-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bs-no-result {
    padding: 16px 12px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.82rem;
}
</style>
